/**
 * Returns a function that runs at most once during a given time window.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window in milliseconds.
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.mainProductForm = this.section?.querySelector('.js-product');
        this.mainForm = this.mainProductForm?.querySelector('.js-product-form');
        this.mainVariantPicker = this.section?.querySelector('variant-picker');
        this.buyButtons = this.section?.querySelector('buy-buttons');
        this.priceContainer = this.querySelector('.sticky-atc__price');
        this.lightBox = this.querySelector('.sticky-atc__listbox-scroll');
        this.variantSelects = Array.from(this.querySelectorAll('custom-select[data-sticky-option-index]'));
        this.isSyncingFromMain = false;

        const priceDataEl = this.querySelector('[data-variant-prices]');
        this.variantPrices = priceDataEl ? JSON.parse(priceDataEl.textContent) : {};

        this.throttledOnScroll = throttle(StickyAtc.handleScroll.bind(this));
        this.updateBarMetrics = this.updateBarMetrics.bind(this);
        this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);

        window.addEventListener('scroll', this.throttledOnScroll);
        window.addEventListener('resize', this.updateBarMetrics);
        document.addEventListener('pointerdown', this.onDocumentPointerDown, true);

        // Keep the sticky UI as a mirror of the main variant picker.
        // The main <variant-picker> remains the single source of truth.
        this.section?.addEventListener('on:variant:change', this.onVariantChange.bind(this));
        this.section?.addEventListener('on:media-gallery:change', this.updateImage.bind(this));

        this.variantSelects.forEach((select) => {
          select.addEventListener('change', this.onVariantSelectChange.bind(this));
        });

        this.addEventListener('click', this.onStickyClick.bind(this));

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.updateBarMetrics);
          this.resizeObserver.observe(this);
        }

        this.updateImage();
        this.updateBarMetrics();

        // custom-select.js is loaded with defer and may be parsed after sticky-atc.js.
        // Do not call custom-select methods immediately until the element is upgraded.
        this.runAfterCustomSelectReady(() => {
          this.prepareStickySelectsForDrawer();
          this.syncStickySelectorsFromMain();
          this.syncStickyAvailabilityFromMain();
        });
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
        window.removeEventListener('resize', this.updateBarMetrics);
        document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
        this.resizeObserver?.disconnect();
      }

      /**
       * Runs sticky dropdown sync only after <custom-select> has been upgraded.
       * Without this guard, stickySelect.selectOption can be undefined because
       * custom-select.js is also loaded with defer and may execute later.
       * @param {Function} callback - Work to run after custom-select is ready.
       */
      runAfterCustomSelectReady(callback) {
        if (!this.variantSelects.length) {
          callback();
          return;
        }

        if (customElements.get('custom-select')) {
          requestAnimationFrame(callback);
          return;
        }

        customElements.whenDefined('custom-select').then(() => {
          requestAnimationFrame(callback);
        });
      }

      /**
       * Adjusts sticky-only custom-select behavior for the drawer layout.
       * The theme's default custom-select closes on listbox blur. That makes the
       * sticky drawer collapse when trying to inspect it in DevTools. For sticky
       * ATC only, blur-close is disabled and replaced with an explicit outside
       * pointerdown close handled by onDocumentPointerDown().
       */
      prepareStickySelectsForDrawer() {
        this.variantSelects.forEach((select) => {
          select.handleBlur = () => {};
        });
      }

      /**
       * Closes sticky dropdown drawers when the customer clicks outside the open
       * selector. Clicking DevTools does not fire a document pointerdown, so the
       * drawer remains inspectable while still closing normally on page clicks.
       * @param {PointerEvent} evt - Pointer event from the document.
       */
      onDocumentPointerDown(evt) {
        const targetSelect = evt.target.closest?.('sticky-atc custom-select[data-sticky-option-index]');
        const targetButton = evt.target.closest?.('.sticky-atc__select-btn');

        // The theme's custom-select opens on button mousedown but does not toggle
        // closed on a second button click. Because we disable blur-close for DevTools
        // inspection, we add a sticky-only toggle here.
        if (targetSelect && targetButton && targetSelect.classList.contains('is-open')) {
          evt.preventDefault();
          evt.stopPropagation();
          targetSelect.hideListbox?.();
          return;
        }

        if (!this.contains(evt.target)) {
          this.closeOpenStickySelects();
          return;
        }

        // If another sticky selector is clicked, close the previously open drawer
        // first. This prevents multiple sticky drawers from staying open together.
        this.variantSelects.forEach((select) => {
          if (select !== targetSelect && select.classList.contains('is-open')) {
            select.hideListbox?.();
          }
        });
      }

      /**
       * Closes every open sticky custom-select drawer.
       */
      closeOpenStickySelects() {
        this.variantSelects.forEach((select) => {
          if (select.classList.contains('is-open')) {
            select.hideListbox?.();
          }
        });
      }

      /**
       * Safely selects a custom-select option.
       * Uses the theme's native selectOption() method when available. If the
       * element is not upgraded yet, it falls back to the same essential DOM
       * updates so the sticky UI never throws a console error.
       * @param {Element} customSelect - <custom-select> element.
       * @param {Element} option - .custom-select__option element.
       * @param {object} [config] - Selection behavior.
       * @param {boolean} [config.dispatchChange=true] - Dispatch change event.
       */
      selectCustomSelectOption(customSelect, option, { dispatchChange = true } = {}) {
        if (!customSelect || !option) return;

        if (typeof customSelect.selectOption === 'function') {
          if (dispatchChange) {
            customSelect.selectOption(option);
          } else {
            this.manuallySelectCustomOption(customSelect, option, false);
          }
          return;
        }

        this.manuallySelectCustomOption(customSelect, option, dispatchChange);
      }

      /**
       * Manual fallback for updating a custom-select without depending on its
       * prototype method. This protects against script-order race conditions.
       * @param {Element} customSelect - <custom-select> element.
       * @param {Element} option - .custom-select__option element.
       * @param {boolean} dispatchChange - Whether to dispatch a change event.
       */
      manuallySelectCustomOption(customSelect, option, dispatchChange) {
        const previousOption = customSelect.querySelector('.custom-select__option[aria-selected="true"]');
        const button = customSelect.querySelector('.custom-select__btn');
        const listbox = customSelect.querySelector('.custom-select__listbox');
        const nativeSelect = document.getElementById(`${customSelect.id}-native`);

        if (previousOption && previousOption !== option) {
          previousOption.setAttribute('aria-selected', 'false');
        }

        option.setAttribute('aria-selected', 'true');
        customSelect.selectedOption = option;

        if (button) {
          const label = option.querySelector('.sticky-atc__option-name') || option.firstElementChild || option;
          if (button.firstElementChild) {
            button.firstElementChild.textContent = label.textContent.trim();
          }

          if ('swatch' in option.dataset) {
            button.dataset.swatch = option.dataset.swatch || '';

            const nativeSwatchColor = option.style.getPropertyValue('--native-swatch-color');
            const nativeSwatchImage = option.style.getPropertyValue('--native-swatch-image');

            if (nativeSwatchColor) {
              button.style.setProperty('--native-swatch-color', nativeSwatchColor);
              button.style.setProperty('--native-swatch-image', '');
            }

            if (nativeSwatchImage) {
              button.style.setProperty('--native-swatch-image', nativeSwatchImage);
              button.style.setProperty('--native-swatch-color', '');
            }
          }
        }

        if (listbox) {
          listbox.setAttribute('aria-activedescendant', option.id);
        }

        if (nativeSelect) {
          nativeSelect.value = option.dataset.value;
          nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (dispatchChange) {
          customSelect.dispatchEvent(new CustomEvent('change', {
            bubbles: true,
            detail: {
              selectedValue: option.dataset.value,
              productUrl: option.dataset.productUrl
            }
          }));
        }
      }

      /**
       * Keeps the fixed drawer positioned exactly above the sticky bar.
       */
      updateBarMetrics() {
        this.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`);
        // Ensure lightBox exists before reading its offsetHeight
        if (this.lightBox) {
          this.style.setProperty('--sticky-atc-lightbox-max-height', `${this.lightBox.offsetHeight}px`);
        }
      }

      /**
       * Handles clicks inside sticky ATC, including the custom drawer close button.
       * @param {Event} evt - Click event.
       */
      onStickyClick(evt) {
        const closeButton = evt.target.closest('[data-sticky-select-close]');
        if (!closeButton) return;

        evt.preventDefault();
        const customSelect = closeButton.closest('custom-select');
        customSelect?.hideListbox?.();
      }

      /**
       * Handles submission of the sticky product form.
       * The sticky form copies the main form data before AJAX add-to-cart, so
       * line item properties, quantity, and the selected variant stay consistent.
       * @param {Event} evt - Submit event.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        const mainIdInput = this.mainForm?.querySelector('[name="id"]');
        const stickyIdInput = this.form?.querySelector('[name="id"]');
        const variantId = mainIdInput?.value || stickyIdInput?.value;

        if (!variantId) {
          const target = this.section?.querySelector('#product-info');
          if (target && target.offsetParent) {
            theme.scrollToRevealElement(target);
          }
          return;
        }

        if (stickyIdInput) {
          stickyIdInput.value = variantId;
        }

        // Validate the real main product form. Do not validate the sticky mirror.
        if (this.mainProductForm?.validate && this.mainProductForm?.form) {
          const customFormValid = this.mainProductForm.validate();
          if (!customFormValid || !this.mainProductForm.form.reportValidity()) {
            if (!customFormValid) {
              theme.scrollToRevealElement(this.mainProductForm);
            } else {
              const input = Array.from(this.mainProductForm.form.elements).find((el) => !el.checkValidity());
              setTimeout(() => theme.scrollToRevealElement(input), 100);
            }
            return;
          }
        }

        // Copy all main form fields into the sticky form before calling the theme's
        // original ProductForm AJAX handler. This preserves properties and quantity.
        this.form.querySelectorAll('[data-copied]').forEach((el) => el.remove());
        const formData = new FormData(this.mainForm || this.form);
        for (const p of formData) {
          if (!this.form.querySelector(`[name="${CSS.escape(p[0])}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.form.append(input);
          }
        }

        super.handleSubmit(evt);
      }

      /**
       * Determines sticky ATC visibility after scroll.
       */
      static handleScroll() {
        if (!this.buyButtons) return;

        const topOffset = document.querySelector('.pageheader--sticky')
          ? document.querySelector('.section-header').offsetHeight
          : 0;

        this.classList.toggle(
          'sticky-atc--out',
          this.buyButtons.getBoundingClientRect().bottom > topOffset
        );

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );

        this.updateBarMetrics();
      }

      /**
       * Handles variant selection from a sticky dropdown.
       * It does not calculate variants. It triggers the matching option in the
       * main variant picker, letting the theme update price, media, URL, and form ID.
       * @param {Event} evt - Change event from sticky custom-select.
       */
      onVariantSelectChange(evt) {
        if (this.isSyncingFromMain) return;

        const stickySelect = evt.target.closest('custom-select[data-sticky-option-index]');
        const selectedOption = stickySelect?.selectedOption
          || stickySelect?.querySelector('.custom-select__option[aria-selected="true"]');
        if (!stickySelect || !selectedOption || !selectedOption.dataset.value) return;

        this.selectMainOption(
          Number(stickySelect.dataset.stickyOptionIndex),
          stickySelect.dataset.stickyOptionName,
          selectedOption.dataset.value
        );
      }

      /**
       * Selects the matching option in the main theme variant picker.
       * @param {number} optionIndex - Zero-based option index.
       * @param {string} optionName - Shopify option name.
       * @param {string} optionValue - Selected option value.
       */
      selectMainOption(optionIndex, optionName, optionValue) {
        if (!this.mainVariantPicker) return;

        const mainSelector = this.getMainOptionSelector(optionIndex, optionName);
        if (!mainSelector) return;

        if (mainSelector.dataset.selectorType === 'dropdown') {
          const mainSelect = mainSelector.querySelector('custom-select');
          const mainOption = Array.from(mainSelector.querySelectorAll('.custom-select__option'))
            .find((option) => option.dataset.value === optionValue);

          if (mainSelect && mainOption) {
            this.selectCustomSelectOption(mainSelect, mainOption, { dispatchChange: true });
          }
        } else {
          const mainInput = Array.from(mainSelector.querySelectorAll('input[type="radio"]'))
            .find((input) => input.value === optionValue);

          if (mainInput) {
            mainInput.click();
          }
        }
      }

      /**
       * Returns the matching option selector from the main variant picker.
       * Index is preferred because different options can share the same value text.
       * @param {number} optionIndex - Zero-based option index.
       * @param {string} optionName - Shopify option name.
       * @returns {?Element}
       */
      getMainOptionSelector(optionIndex, optionName) {
        const selectors = Array.from(
          this.mainVariantPicker?.querySelectorAll('.option-selector:not(.option-selector--custom)') || []
        );

        return selectors[optionIndex] || selectors.find((selector) => selector.dataset.option === optionName) || null;
      }

      /**
       * Handles a variant change dispatched by the main <variant-picker>.
       * @param {CustomEvent} evt - Variant change event.
       */
      onVariantChange(evt) {
        const idInput = this.form?.querySelector('[name="id"]');

        if (evt.detail.variant) {
          if (idInput) idInput.value = evt.detail.variant.id;
          this.updatePriceDisplay(evt.detail.variant);
        } else {
          if (idInput) idInput.value = '';
          if (this.priceContainer) this.priceContainer.hidden = true;
        }

        this.syncStickySelectorsFromMain(evt.detail.selectedOptions);
        this.syncStickyAvailabilityFromMain();
        this.updateImage();
        this.updateAddToCartButton(evt.detail.variant);
        this.setErrorMsgState();
        this.updateBarMetrics();
      }

      /**
       * Mirrors selected option values from the main picker into the sticky dropdowns.
       * @param {?Array} selectedOptions - Selected values from main variant-picker.
       */
      syncStickySelectorsFromMain(selectedOptions = null) {
        const mainSelectedOptions = selectedOptions || this.mainVariantPicker?.selectedOptions;
        if (!mainSelectedOptions || !this.variantSelects.length) return;

        this.isSyncingFromMain = true;

        try {
          this.variantSelects.forEach((stickySelect) => {
            const optionIndex = Number(stickySelect.dataset.stickyOptionIndex);
            const selectedValue = mainSelectedOptions[optionIndex];
            if (!selectedValue) return;

            const stickyOption = Array.from(stickySelect.querySelectorAll('.custom-select__option'))
              .find((option) => option.dataset.value === selectedValue);

            if (stickyOption && stickySelect.selectedOption !== stickyOption) {
              // Mirror the main value silently. Do not dispatch a sticky change event,
              // otherwise the sticky picker would trigger the main picker again.
              this.selectCustomSelectOption(stickySelect, stickyOption, { dispatchChange: false });
            }
          });
        } finally {
          this.isSyncingFromMain = false;
        }
      }

      /**
       * Mirrors unavailable/nonexistent classes from the main picker into sticky options.
       * This keeps sticky option styling aligned with the theme availability engine.
       */
      syncStickyAvailabilityFromMain() {
        if (!this.mainVariantPicker || !this.variantSelects.length) return;

        this.variantSelects.forEach((stickySelect) => {
          const optionIndex = Number(stickySelect.dataset.stickyOptionIndex);
          const optionName = stickySelect.dataset.stickyOptionName;
          const mainSelector = this.getMainOptionSelector(optionIndex, optionName);
          if (!mainSelector) return;

          stickySelect.querySelectorAll('.custom-select__option').forEach((stickyOption) => {
            const value = stickyOption.dataset.value;
            const mainOption = mainSelector.dataset.selectorType === 'dropdown'
              ? Array.from(mainSelector.querySelectorAll('.custom-select__option')).find((option) => option.dataset.value === value)
              : Array.from(mainSelector.querySelectorAll('input[type="radio"]')).find((input) => input.value === value);

            stickyOption.classList.toggle('is-unavailable', !!mainOption?.classList.contains('is-unavailable'));
            stickyOption.classList.toggle('is-nonexistent', !!mainOption?.classList.contains('is-nonexistent'));
          });
        });
      }

      /**
       * Updates the sticky price display.
       * @param {object} variant - Current variant object.
       */
      updatePriceDisplay(variant) {
        if (this.priceContainer && variant && this.variantPrices[variant.id]) {
          this.priceContainer.textContent = this.variantPrices[variant.id].price;
          this.priceContainer.hidden = false;
        }
      }

      /**
       * Reserved for compatibility with media gallery events.
       */
      updateImage() {
        // Sticky ATC uses the swatch thumbnail inside the dropdown button.
        // Product media remains controlled by the main variant picker/gallery.
      }

      /**
       * Mirrors the main add-to-cart state into the sticky add-to-cart button.
       * @param {?object} variant - Current variant object.
       */
      updateAddToCartButton(variant) {
        if (!this.submitBtn) return;

        const mainButton = this.mainForm?.querySelector('[name="add"]');
        const variantAvailable = variant && variant.available;
        const unavailableStr = variant ? theme.strings.noStock : this.submitBtn.dataset.unavailableText;

        this.submitBtn.disabled = !variantAvailable;
        this.submitBtn.textContent = mainButton?.textContent?.trim() || (
          variantAvailable ? this.submitBtn.dataset.addToCartText : unavailableStr
        );
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}
